// BoxLines — a party's box in the desk's words, before → after.
//
//   AED · owes you 1,000 → square
//   USDT · you hold 5,000
//
// Item 70 / §5.7: Between parties had the right one; Record money said
// "AED +1,000 → +0 · they owe the desk", Adjust said "Current balance: −1,000
// AED", Book trade said "AED: owes you 52,728". One line, one vocabulary.
//
// THE RULE IT ENFORCES (§6 "A party's box", §7 rule 10): positive = "owes you",
// negative = "you hold", under dust = "square". No sign is ever printed — the
// WORDS carry the direction, and the one negative token (`text-neg`) only
// underlines what the words already say (SignLaw). Dust and the number format
// are the ones every picker row and the party page use (`dustFor`,
// `fmtAmount`), so one party reads the same on every screen.
//
// EMPHASIS (14 Sept 2026, the rule in styles.css): the asset and the words
// stay muted; the figure is `.amt`; the figure that answers — the box AFTER
// the entry, or the box as it stands when there is no after — is `.amt-key`
// in full text colour, so the eye lands on the number the screen is about.
//
// Usage:
//   <BoxLines asset="AED" before={1000} after={0} />        // AED · owes you 1,000 → square
//   <BoxLines asset="USDT" before={-5000} />                // USDT · you hold 5,000
//   `Loki now: ${boxWords(36720, 'AED')} AED`               // for a success line

import { displayAsset, fmtAmount } from '../lib/format';
import { dustFor, foldAsset } from '../lib/naming';

/** The words and the figure apart, so a line can set the figure in mono and
 *  leave the words in the sentence's own face. `figure` is null for square. */
export function boxParts(value: number, asset: string): { words: 'owes you' | 'you hold' | 'square'; figure: string | null } {
  if (!Number.isFinite(value) || Math.abs(value) <= dustFor(asset)) return { words: 'square', figure: null };
  return { words: value > 0 ? 'owes you' : 'you hold', figure: fmtAmount(Math.abs(value), foldAsset(asset)) };
}

/** "owes you 1,000" · "you hold 1,000" · "square" — without the asset, so the
 *  caller places the asset once, where the line wants it. */
export function boxWords(value: number, asset: string): string {
  const { words, figure } = boxParts(value, asset);
  return figure ? `${words} ${figure}` : words;
}

/** The words with the figure set apart — for a row that is not a BoxLines
 *  line but says the same thing (Settle later's box, Book trade's party
 *  line). `answer` makes the figure the answering one. */
export function BoxWords({ value, asset, answer = false }: { value: number; asset: string; answer?: boolean }) {
  const { words, figure } = boxParts(value, asset);
  if (!figure) return <span className="text-tg-hint">square</span>;
  const tone = value < 0 ? 'text-neg' : 'text-tg-text';
  return (
    <span className={tone}>
      {words} <span className={answer ? 'amt-key' : 'amt'}>{figure}</span>
    </span>
  );
}

export function BoxLines({ asset, before, after, className = '' }: {
  asset: string;
  before: number;
  /** The box once this entry posts. Omit to show the box as it stands. */
  after?: number;
  className?: string;
}) {
  return (
    <p className={`text-meta text-tg-hint ${className}`}>
      {displayAsset(asset)}{' · '}
      <BoxWords value={before} asset={asset} answer={after === undefined} />
      {after !== undefined && (
        <>
          {' → '}
          <BoxWords value={after} asset={asset} answer />
        </>
      )}
    </p>
  );
}
